import { useState } from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { usePageTitle } from "@/hooks/usePageTitle";
import { formatAmount } from "@/utils/formatAmount";
import { JvInvoicesPanel } from "@/features/finances/components/JvInvoicesPanel";
import {
  useConsortiumAgreements,
  useCreateConsortiumAgreement,
  useUpdateConsortiumAgreement,
  useDeleteConsortiumAgreement,
  useConsortiumAccessUsers,
  useConsortiumAccessGrants,
  useGrantConsortiumAccess,
  useRevokeConsortiumAccess,
} from "@/features/finances/hooks/useFinances";
import type { ConsortiumAgreement } from "@/features/finances/types";

const EMPTY = {
  name: "",
  partner_name: "",
  our_share_percent: "60.00",
  notes: "",
  is_active: true,
  partner_nip: "",
  partner_krs: "",
  partner_regon: "",
  partner_address: "",
  partner_bank_account: "",
  partner_bank_name: "",
};

export function ConsortiumPage() {
  usePageTitle("Konsorcja");
  const user = useAuthStore((s) => s.user);
  const isCeo = user?.is_ceo === true;
  const hasAccess = user?.has_consortium_access === true;

  const { data: agreements = [], isLoading } = useConsortiumAgreements();
  const createA = useCreateConsortiumAgreement();
  const updateA = useUpdateConsortiumAgreement();
  const deleteA = useDeleteConsortiumAgreement();

  const [editing, setEditing] = useState<ConsortiumAgreement | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY);
    setShowForm(true);
  }
  function openEdit(a: ConsortiumAgreement) {
    setEditing(a);
    setForm({
      name: a.name,
      partner_name: a.partner_name,
      our_share_percent: a.our_share_percent,
      notes: a.notes,
      is_active: a.is_active,
      partner_nip: a.partner_nip,
      partner_krs: a.partner_krs,
      partner_regon: a.partner_regon,
      partner_address: a.partner_address,
      partner_bank_account: a.partner_bank_account,
      partner_bank_name: a.partner_bank_name,
    });
    setShowForm(true);
  }
  function submit() {
    const payload = { ...form };
    if (editing) updateA.mutate({ id: editing.id, data: payload }, { onSuccess: () => setShowForm(false) });
    else createA.mutate(payload, { onSuccess: () => setShowForm(false) });
  }

  if (!hasAccess) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">Brak dostępu do konsorcjów.</p>;
  }

  return (
    <div className="flex flex-col gap-8">
      {/* Umowy konsorcyjne */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-base font-bold text-gray-900 dark:text-gray-100">Umowy konsorcyjne</h1>
          <Button size="sm" onClick={openCreate}>
            <Plus size={14} /> Nowa umowa
          </Button>
        </div>
        {isLoading ? (
          <p className="text-sm text-gray-400">Ładowanie…</p>
        ) : agreements.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">Brak umów konsorcyjnych.</p>
        ) : (
          <div className="bg-white dark:bg-gray-800 overflow-x-auto border border-gray-100 dark:border-gray-700">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest">Nazwa</th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest">Partner</th>
                  <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest">Nasz udział</th>
                  <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest">Faktur</th>
                  <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest">Obrót netto</th>
                  <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest">Nasza część</th>
                  <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest">Partnera</th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest">Status</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {agreements.map((a) => (
                  <tr key={a.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-2 font-medium text-gray-800 dark:text-gray-200">{a.name}</td>
                    <td className="px-4 py-2 text-gray-600 dark:text-gray-400">{a.partner_name}</td>
                    <td className="px-4 py-2 text-right text-gray-700 dark:text-gray-300">{a.our_share_percent}%</td>
                    <td className="px-4 py-2 text-right text-gray-500 dark:text-gray-400">{a.invoices_count}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">
                      {formatAmount(a.invoices_net_total)}
                    </td>
                    <td
                      className="px-4 py-2 text-right tabular-nums font-semibold text-gray-800 dark:text-gray-200"
                      title={
                        a.attached_partner_invoices > 0
                          ? `${a.attached_partner_invoices} z ${a.invoices_count} faktur ma dopięte FV partnera — tam podział jest realny, nie umowny`
                          : `Wyliczone z zapisanego udziału ${a.our_share_percent}% — żadna faktura nie ma jeszcze dopiętej FV partnera`
                      }
                    >
                      {formatAmount(a.our_net_total)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-500 dark:text-gray-400">
                      {formatAmount(a.partner_net_total)}
                    </td>
                    <td className="px-4 py-2">
                      <Badge color={a.is_active ? "green" : "gray"}>{a.is_active ? "Aktywna" : "Nieaktywna"}</Badge>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => openEdit(a)} className="text-gray-400 hover:text-[var(--color-primary)]" title="Edytuj">
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => {
                            if (confirm("Dezaktywować umowę konsorcyjną?")) deleteA.mutate(a.id);
                          }}
                          className="text-gray-400 hover:text-red-600"
                          title="Dezaktywuj"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Faktury partnerów JV — widok dla księgowości, z linkiem do FV-matki */}
      <JvInvoicesPanel />

      {/* Dostęp do konsorcjów — tylko CEO */}
      {isCeo && <ConsortiumAccessPanel />}

      <Modal open={showForm} onClose={() => setShowForm(false)} title={editing ? "Edytuj umowę konsorcyjną" : "Nowa umowa konsorcyjna"} size="md">
        <div className="flex flex-col gap-3">
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
            Nazwa umowy
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-sm" />
          </label>
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
            Partner
            <input value={form.partner_name} onChange={(e) => setForm({ ...form, partner_name: e.target.value })} placeholder="np. itB Legal" className="mt-1 w-full border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-sm" />
          </label>
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
            Nasz udział (%)
            <input type="number" step="0.01" min="0" max="100" value={form.our_share_percent} onChange={(e) => setForm({ ...form, our_share_percent: e.target.value })} className="mt-1 w-full border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-sm" />
          </label>

          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mt-1">Dane partnera (do rozliczeń)</p>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
              NIP
              <input value={form.partner_nip} onChange={(e) => setForm({ ...form, partner_nip: e.target.value })} className="mt-1 w-full border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-sm" />
            </label>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
              KRS
              <input value={form.partner_krs} onChange={(e) => setForm({ ...form, partner_krs: e.target.value })} className="mt-1 w-full border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-sm" />
            </label>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
              REGON
              <input value={form.partner_regon} onChange={(e) => setForm({ ...form, partner_regon: e.target.value })} className="mt-1 w-full border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-sm" />
            </label>
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
              Bank
              <input value={form.partner_bank_name} onChange={(e) => setForm({ ...form, partner_bank_name: e.target.value })} className="mt-1 w-full border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-sm" />
            </label>
          </div>
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
            Numer konta
            <input value={form.partner_bank_account} onChange={(e) => setForm({ ...form, partner_bank_account: e.target.value })} className="mt-1 w-full border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-sm" />
          </label>
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
            Adres
            <input value={form.partner_address} onChange={(e) => setForm({ ...form, partner_address: e.target.value })} className="mt-1 w-full border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-sm" />
          </label>

          <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
            Notatki
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1 w-full border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-sm" />
          </label>
          <label className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-gray-400">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="accent-[var(--color-primary)]" />
            Aktywna
          </label>
          <div className="flex justify-end gap-2 mt-1">
            <Button variant="secondary" size="sm" onClick={() => setShowForm(false)}>Anuluj</Button>
            <Button size="sm" loading={createA.isPending || updateA.isPending} disabled={!form.name || !form.partner_name} onClick={submit}>
              {editing ? "Zapisz" : "Utwórz"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function ConsortiumAccessPanel() {
  const { data: users = [] } = useConsortiumAccessUsers();
  const { data: grants = [] } = useConsortiumAccessGrants();
  const grant = useGrantConsortiumAccess();
  const revoke = useRevokeConsortiumAccess();
  const grantByUser = new Map(grants.map((g) => [g.user, g.id]));

  return (
    <section>
      <h2 className="text-base font-bold text-gray-900 dark:text-gray-100 mb-1">Dostęp do konsorcjów</h2>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
        CEO ma dostęp zawsze. Tu nadajesz dostęp innym osobom (do umów i dopinania FV partnera).
      </p>
      <div className="bg-white dark:bg-gray-800 overflow-x-auto border border-gray-100 dark:border-gray-700">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400">
            <tr>
              <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest">Osoba</th>
              <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest">Rola</th>
              <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest">Dostęp</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
            {users.map((row) => (
              <tr key={row.user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-4 py-2 text-gray-800 dark:text-gray-200">
                  {row.user.first_name} {row.user.last_name}
                </td>
                <td className="px-4 py-2 text-gray-500 dark:text-gray-400">{row.user.role}</td>
                <td className="px-4 py-2">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={row.has_access}
                      onChange={() => {
                        if (row.has_access) {
                          const gid = grantByUser.get(row.user.id);
                          if (gid) revoke.mutate(gid);
                        } else {
                          grant.mutate(row.user.id);
                        }
                      }}
                      className="w-4 h-4 accent-[var(--color-primary)]"
                    />
                    <span className="text-xs text-gray-600 dark:text-gray-400">{row.has_access ? "Przyznany" : "Brak"}</span>
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
